import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  CircleMarker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { LocateFixed, Navigation } from "lucide-react";
import L from "leaflet";
import ATTENDANCE_MAP_CONFIG from "../../config/mapConfig";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const FALLBACK_CENTER = {
  lat: ATTENDANCE_MAP_CONFIG.DEFAULT_CENTER_LAT,
  lng: ATTENDANCE_MAP_CONFIG.DEFAULT_CENTER_LNG,
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function toCoord(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function MapResizeFix() {
  const map = useMap();
  useEffect(() => {
    const fix = () => map.invalidateSize();
    const t1 = setTimeout(fix, 80);
    const t2 = setTimeout(fix, 400);
    window.addEventListener("resize", fix);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", fix);
    };
  }, [map]);
  return null;
}

function MapFlyTo({ lat, lng, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (lat == null || lng == null) return;
    map.flyTo([lat, lng], zoom ?? 16, { duration: 0.65 });
  }, [lat, lng, zoom, map]);
  return null;
}

function MapClickHandler({ onPositionChange }) {
  useMapEvents({
    click(e) {
      onPositionChange?.({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function readGeolocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }),
      (err) => {
        if (err?.code === 1) reject(new Error("Location permission denied"));
        else if (err?.code === 2) reject(new Error("Location unavailable"));
        else if (err?.code === 3) reject(new Error("Location timed out"));
        else reject(new Error("Could not read location"));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

export default function AttendanceLocationMapPicker({
  latitude,
  longitude,
  radiusMeters = 100,
  onPositionChange,
  autoLocate = false,
}) {
  const pinLat = toCoord(latitude);
  const pinLng = toCoord(longitude);
  const hasPin = pinLat != null && pinLng != null;
  const autoLocatedRef = useRef(false);

  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState("");

  const placePinAt = useCallback(
    (pos) => {
      const lat = toCoord(pos.lat);
      const lng = toCoord(pos.lng);
      if (lat == null || lng == null) return;
      onPositionChange?.({ lat, lng });
    },
    [onPositionChange],
  );

  const locateUser = useCallback(
    async ({ silent = false } = {}) => {
      setLocating(true);
      if (!silent) setStatus("");
      try {
        const pos = await readGeolocation();
        setUserLocation(pos);
        placePinAt(pos);
        if (!silent) setStatus("Pin placed at your current location");
      } catch (err) {
        if (!silent) {
          setStatus(err?.message || "Could not access location — tap the map instead");
        }
      } finally {
        setLocating(false);
      }
    },
    [placePinAt],
  );

  useEffect(() => {
    if (!autoLocate || autoLocatedRef.current) return;
    autoLocatedRef.current = true;
    locateUser({ silent: true });
  }, [autoLocate, locateUser]);

  const mapCenter = useMemo(() => {
    if (hasPin) return { lat: pinLat, lng: pinLng };
    if (userLocation) return { lat: userLocation.lat, lng: userLocation.lng };
    return FALLBACK_CENTER;
  }, [hasPin, pinLat, pinLng, userLocation]);

  const mapZoom = hasPin ? 17 : userLocation ? 16 : 11;

  return (
    <div className="att-map-panel">
      <div className="att-map-panel__header">
        <div className="att-map-panel__status">
          <Navigation size={15} aria-hidden />
          <span>
            {hasPin
              ? `Check-in zone: ${radiusMeters} m radius`
              : "Click the map or use My location to drop a pin"}
          </span>
        </div>
        <button
          type="button"
          className="att-map-panel__locate-btn"
          onClick={() => locateUser({ silent: false })}
          disabled={locating}
        >
          <LocateFixed size={15} aria-hidden />
          {locating ? "Locating…" : "My location"}
        </button>
      </div>

      {status ? <p className="att-map-panel__feedback">{status}</p> : null}

      <div className="attendance-map-wrap">
        <MapContainer
          center={[mapCenter.lat, mapCenter.lng]}
          zoom={mapZoom}
          scrollWheelZoom
          className="att-leaflet-map"
        >
          <TileLayer
            attribution={ATTENDANCE_MAP_CONFIG.TILE_ATTRIBUTION}
            url={ATTENDANCE_MAP_CONFIG.TILE_URL}
            subdomains={ATTENDANCE_MAP_CONFIG.TILE_SUBDOMAINS}
            maxZoom={19}
          />
          <MapResizeFix />
          {hasPin ? <MapFlyTo lat={pinLat} lng={pinLng} zoom={mapZoom} /> : null}
          <MapClickHandler onPositionChange={onPositionChange} />

          {userLocation ? (
            <CircleMarker
              center={[userLocation.lat, userLocation.lng]}
              radius={7}
              pathOptions={{
                color: "#0369a1",
                fillColor: "#0ea5e9",
                fillOpacity: 1,
                weight: 2,
              }}
            />
          ) : null}

          {hasPin ? (
            <>
              <Marker key={`pin-${pinLat}-${pinLng}`} position={[pinLat, pinLng]} />
              <Circle
                key={`circle-${pinLat}-${pinLng}-${radiusMeters}`}
                center={[pinLat, pinLng]}
                radius={radiusMeters}
                pathOptions={{
                  color: "#0f766e",
                  fillColor: "#14b8a6",
                  fillOpacity: 0.22,
                  weight: 2.5,
                }}
              />
            </>
          ) : null}
        </MapContainer>
      </div>
    </div>
  );
}
